import * as Yup from 'yup';
import * as vp from 'vopay';
import { useMemo, useEffect, useState, ReactNode, JSX } from 'react';
import { ObjectShape } from 'yup/lib/object';
import { FormikValues, useFormik } from 'formik';
import { FieldData, FormCtx, FormikOptions, InputState } from './types';

function getValidationSchema<T>(
  ctx: FormCtx<T>,
  fieldDataList: FieldData<T>[],
  initalValidationSchema?: { [key: string]: any },
): Yup.ObjectSchema<ObjectShape> {
  const validations = fieldDataList
    .filter(
      (fieldData) =>
        fieldData.validate && fieldData.showField(ctx.values) && fieldData.getInputState(ctx) !== 'disabled',
    )
    .reduce(
      (existingValidations, fieldData) => ({
        ...existingValidations,
        [fieldData.fieldName]: fieldData.validate && fieldData.validate.call(fieldData, ctx),
      }),
      { ...initalValidationSchema },
    );

  return Yup.object().shape(validations);
}

export function RequiredIcon() {
  // return <Icon icon="asterisk" color="error" height="8px" width="8px" />;
  return (
    <span
      style={{
        color: 'red',
      }}
    >
      *
    </span>
  );
}

function formikHandlerField<T, K, J>(
  ctx: FormCtxWithReviewing<T>,
  fieldData: FieldData<T>,
  fieldOptions: FormikOptions<K, J>,
  completeDataList: FieldData<T>[],
) {
  return {
    onFocus: (event: React.FocusEvent<any>) => {
      // loop though all previous   and set to as touched

      const currentIndex = completeDataList.findIndex((data) => data.fieldName === fieldData.fieldName);

      if (currentIndex !== -1) {
        // Check if the current field was found
        completeDataList.forEach((data, index) => {
          // Set touched for all fields up to the current index
          if (index < currentIndex) {
            ctx.setFieldTouched(data.fieldName, true, true);
          }
        });
      }
      if (hasOnFocus(fieldOptions)) {
        fieldOptions.onFocus(event);
      }
      function hasOnFocus(
        obj: Record<string, any>,
      ): obj is { onFocus: (event: React.FocusEvent<HTMLElement>) => void } {
        return 'onFocus' in obj;
      }
    },
    onBlur: (event: React.FocusEvent<any>) => {
      ctx.handleBlur(event);

      if (hasBlur(fieldOptions)) {
        fieldOptions.onBlur(event);
      }

      function hasBlur(obj: Record<string, any>): obj is { onBlur: (event: React.FocusEvent<HTMLElement>) => void } {
        return 'onBlur' in obj;
      }
    },
    onChange: (event: React.ChangeEvent<any>, data?: any) => {
      ctx.handleChange(event);

      if (fieldOptions.onChange) {
        fieldOptions.onChange(event as any, data);
      }
    },
    disabled: getVisualInputState(ctx, fieldData) === 'disabled',
    name: String(fieldData.fieldName),
    error:
      ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
        ? ctx.errors[fieldData.fieldName]?.toString()
        : null,
    errorMsg:
      ctx.errors[fieldData.fieldName] && ctx.touched[fieldData.fieldName]
        ? ctx.errors[fieldData.fieldName]?.toString()
        : undefined,
    value: fieldOptions.formatValue
      ? fieldOptions.formatValue(ctx.values[fieldData.fieldName] as unknown as J)
      : (ctx.values[fieldData.fieldName] as unknown as K),
  };
}

function getVisualInputState<T>(ctx: FormCtxWithReviewing<T>, fieldData: FieldData<T>): InputState {
  if (ctx.isReviewing || ctx.isSubmitting) return 'disabled';

  return fieldData.getInputState(ctx);
}

export function renderDefaultFormElement(templateData: TemplateData): JSX.Element {
  const { ElementContainer, Box } = vp;
  const showingLabel = templateData.label || templateData.inputState === 'required' || templateData.tooltip;

  return (
    <ElementContainer
      key={templateData.fieldName}
      label={
        showingLabel ? (
          <Box ai="center" gapSize={templateData.inputState === 'required' ? '2px' : '4px'}>
            {templateData.label &&
              (templateData.label || (
                // Keep the space so it aligns with other inputs on the same line
                <span>&nbsp;</span>
              ))}
            {templateData.inputState === 'required' && <RequiredIcon />}
            {templateData.tooltip && templateData.tooltip}
          </Box>
        ) : null
      }
      padding="0px 0px 8px"
      isBold={false}
      input={<Box flx="col">{templateData.template}</Box>}
    />
  );
}

export type TemplateData = {
  fieldName: string;
  inputState: InputState;
  label: ReactNode;
  tooltip: ReactNode;
  template: ReactNode;
};

type FormCtxWithReviewing<T> = FormCtx<T> & { isReviewing: boolean };

function generateTemplateData<T>(
  ctx: FormCtxWithReviewing<T>,
  fieldData: FieldData<T>,
  completeDataList: FieldData<T>[],
): TemplateData {
  return {
    fieldName: fieldData.fieldName,
    inputState: getVisualInputState(ctx, fieldData),
    label: fieldData.label && fieldData.label(ctx),
    tooltip: fieldData.tooltip && fieldData.tooltip(ctx),
    template:
      fieldData.renderField &&
      fieldData.renderField({
        ctx,
        getFormikData: (options) => formikHandlerField(ctx, fieldData, options, completeDataList),
      }),
  };
}

type RenderFormElementsParams<T> = {
  ctx: FormCtxWithReviewing<T>;
  renderFieldDataList: FieldData<T>[];
  completeDataList?: FieldData<T>[];
  override?: (fieldData: TemplateData) => React.ReactNode;
};

function renderFormElements<T>({
  ctx,
  renderFieldDataList,
  completeDataList,
  override,
}: RenderFormElementsParams<T>): React.ReactNode[] {
  const dataList = (completeDataList || renderFieldDataList).filter((fieldData) => fieldData.showField(ctx.values));

  return renderFieldDataList
    .filter((fieldData) => fieldData.showField(ctx.values))
    .map((fieldData) => {
      if (override) {
        return override(generateTemplateData(ctx, fieldData, dataList));
      }

      return renderDefaultFormElement(generateTemplateData(ctx, fieldData, dataList));
    });
}

function serializeForm<T, K extends object>(ctx: FormCtx<T>, fieldDataList: FieldData<T>[]): K {
  return fieldDataList.reduce((serializedForm, fieldData) => {
    return {
      ...serializedForm,
      [fieldData.fieldName]: getFieldValue(),
    };

    function getFieldValue() {
      if (fieldData.getInputState(ctx) === 'disabled') return undefined;

      if (!fieldData.showField(ctx.values)) return undefined;

      if (fieldData.serializeField) return fieldData.serializeField(ctx.values);

      return ctx.values[fieldData.fieldName];
    }
  }, {} as K);
}

type FormProps<T extends FormikValues, Serialized extends object> = {
  config: FieldData<T>[];
  initialValues: T;
  onSubmit: (values: Serialized, ctx: FormCtx<T>) => void | Promise<any>;
  isReviewing?: boolean;
};

export function useForm<T extends FormikValues, Serialized extends object = T>({
  config,
  initialValues,
  onSubmit,
  isReviewing = false,
}: FormProps<T, Serialized>) {
  const [validationSchema, setValidationSchema] = useState<Yup.ObjectSchema<ObjectShape>>(Yup.object().shape({}));

  const formik = useFormik({
    validationSchema,
    initialValues,
    onSubmit: () => {
      const vals = serializeForm<T, Serialized>(formik, config);
      const res = onSubmit(vals, formik);

      // if we return a promise, then isSubmitting will be handled automatically
      // https://stackoverflow.com/a/71158074
      // https://formik.org/docs/api/formik#onsubmit-values-values-formikbag-formikbag--void--promiseany
      if (typeof res === 'object' && typeof res.then === 'function') {
        return res;
      }

      return Promise.resolve(null);
    },
  });

  const ctx = useMemo(
    () => ({
      values: formik.values,
      errors: formik.errors,
      touched: formik.touched,
      handleBlur: formik.handleBlur,
      handleChange: formik.handleChange,
      setFieldTouched: formik.setFieldTouched,
      setFieldValue: formik.setFieldValue,
      setValues: formik.setValues,
      handleSubmit: formik.handleSubmit,
      isValid: formik.isValid,
      isSubmitting: formik.isSubmitting,
      isReviewing,
      resetForm: formik.resetForm,
    }),
    [formik.values, formik.errors, formik.touched, formik.isSubmitting, isReviewing],
  );

  useEffect(() => {
    setValidationSchema(getValidationSchema(ctx, config));
  }, [ctx]);

  useEffect(() => {
    formik.validateForm();
  }, [validationSchema]);
  return {
    ctx,
    renderFormElements: ({
      config: subConfig,
      override,
    }: {
      config: FieldData<T>[];
      override?: (fieldData: TemplateData) => React.ReactNode;
    }) =>
      renderFormElements({
        renderFieldDataList: subConfig,
        completeDataList: config,
        ctx,
        override,
      }),
  };
}
